import {course_getStudents_Api} from 'apis'
import _ from 'lodash'
import useTranslate from 'modules/local/useTranslate'
import React, {useCallback, useContext, useMemo, useState} from 'react'
import {useLocation} from 'react-use'
import {branch, compose} from 'recompose'
import {ContentNotFound} from 'views/AppLocals/ContentNotFound'
import {StudentActionTypes} from 'views/AppLocals/Course'
import EntityList from 'views/AppLocals/EntityList'
import {bindQueryParams} from 'views/AppLocals/functions/routerHelper'
import usePaginationHelper, {createPagerKey} from 'views/AppLocals/hooks/usePaginationHelper'
import {NullAsDefault} from 'views/Shared'
import {StudentFilter} from '../components/filters/StudentFilter'
import {useRouteSearchParam} from '../../../../envs/_current/history'
import {useStudentForm} from '../../../AppLocals/Course/CourseClass/useStudentForm'
import EmptyHolder from 'components/EmptyHolder'
import {Avatar, Checkbox, message, Popover, Tooltip} from 'antd'
import {FaLink, FaLinkSlash, FaUserGraduate} from 'react-icons/fa6'
import {renderIfElse} from '../../../Shared'
import copyToClipboard from 'copy-to-clipboard'
import {IoCopyOutline} from 'react-icons/io5'
import {HiEnvelope, HiPhone} from 'react-icons/hi2'
import {BiSolidEdit, BiTrash} from 'react-icons/bi'
import {RiSearchEyeLine} from 'react-icons/ri'
import createContextMenu from 'views/AppLocals/factory/createContextMenu'
import {formatDate} from '../../../../helpers/datetimeHelpers'
import getLabelOfGender from '../../../../helpers/getLabelOfGender'
import {BsLayoutThreeColumns, BsPersonCheckFill} from 'react-icons/bs'
import classNames from 'classnames'
import {LayoutContext} from '../../../../components/layouts/Default/LayoutContext'
import {GoDotFill} from 'react-icons/go'

const ALL_COLUMNS = [
    {key: 'student',      label: 'student',  alwaysVisible: true},
    {key: 'student_code', label: 'student code'},
    {key: 'father',       label: 'parents'},
    {key: 'contact',      label: 'contact'},
]

const DEFAULT_VISIBLE_COLS = ['student_code', 'father', 'contact']

// ─── Skeleton ─────────────────────────────────────────────────────────────────

const Pulse = ({w = 'w-24', h = 'h-4', rounded = 'rounded'}) => (
    <div className={classNames('animate-pulse bg-gray-100', rounded, h, w)}/>
)

const renderTableLoading = (visibleCols) => () => (
    <>
        {Array.from({length: 6}).map((_, i) => (
            <tr key={i} className="border-t border-color-50">
                <td className="px-3 py-3 text-center">
                    <Pulse w="w-5" h="h-4"/>
                </td>
                <td className="px-4 py-3" style={{minWidth: 220}}>
                    <div className="flex items-start gap-2">
                        <Pulse w="w-9" h="h-9" rounded="rounded-full"/>
                        <div className="flex flex-col gap-1 flex-1">
                            <Pulse w="w-32"/>
                            <Pulse w="w-20" h="h-3"/>
                            <div className="flex items-center gap-1 mt-1">
                                <Pulse w="w-4" h="h-4" rounded="rounded-full"/>
                                <Pulse w="w-24" h="h-3"/>
                            </div>
                        </div>
                    </div>
                </td>
                {visibleCols.includes('student_code') && (
                    <td className="px-3 py-3">
                        <Pulse w="w-20" h="h-5" rounded="rounded-md"/>
                    </td>
                )}
                {visibleCols.includes('father') && (
                    <td className="px-3 py-3">
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center gap-1">
                                <Pulse w="w-5" h="h-3"/>
                                <Pulse w="w-24"/>
                            </div>
                            <div className="flex items-center gap-1">
                                <Pulse w="w-5" h="h-3"/>
                                <Pulse w="w-28"/>
                            </div>
                        </div>
                    </td>
                )}
                {visibleCols.includes('contact') && (
                    <td className="px-3 py-3">
                        <div className="flex flex-col gap-1">
                            <Pulse w="w-24" h="h-3"/>
                            <Pulse w="w-36" h="h-3"/>
                        </div>
                    </td>
                )}
                <td className="py-3" style={{width: 44}}/>
            </tr>
        ))}
    </>
)

const renderCardLoading = () => () => (
    <>
        {Array.from({length: 5}).map((_, i) => (
            <div key={i} className="px-4 py-3 flex gap-3 border-b border-color-50">
                <Pulse w="w-10" h="h-10" rounded="rounded-full"/>
                <div className="flex flex-col gap-2 flex-1">
                    <div className="flex items-center gap-2">
                        <Pulse w="w-32"/>
                        <Pulse w="w-20" h="h-5" rounded="rounded-md"/>
                    </div>
                    <Pulse w="w-24" h="h-3"/>
                    <div className="mt-1 flex flex-col gap-1">
                        <Pulse w="w-48" h="h-3"/>
                        <Pulse w="w-40" h="h-3"/>
                    </div>
                </div>
            </div>
        ))}
    </>
)

// ─── Desktop table ─────────────────────────────────────────────────────────────

const ParentsTable = ({children, t, visibleCols}) => (
    <div className="overflow-hidden">
        <table className="w-full text-sm" style={{minWidth: 600}}>
            <thead>
                <tr className="background-100 border-b border-color-50">
                    <th
                        className="px-3 py-2 text-center text-xs font-semibold text-color-400 uppercase tracking-wide"
                        style={{width: 40}}>
                        #
                    </th>
                    <th
                        className="px-4 py-2 text-left text-xs font-semibold text-color-400 uppercase tracking-wide"
                        style={{minWidth: 220}}>
                        {t('student')}
                    </th>
                    {visibleCols.includes('student_code') && (
                        <th
                            className="px-3 py-2 text-left text-xs font-semibold text-color-400 uppercase tracking-wide"
                            style={{minWidth: 120}}>
                            {t('student code')}
                        </th>
                    )}
                    {visibleCols.includes('father') && (
                        <th
                            className="px-3 py-2 text-left text-xs font-semibold text-color-400 uppercase tracking-wide"
                            style={{minWidth: 180}}>
                            {t('parents')}
                        </th>
                    )}
                    {visibleCols.includes('contact') && (
                        <th
                            className="px-3 py-2 text-left text-xs font-semibold text-color-400 uppercase tracking-wide"
                            style={{minWidth: 180}}>
                            {t('contact')}
                        </th>
                    )}
                    <th style={{width: 44}}/>
                </tr>
            </thead>
            <tbody>{children}</tbody>
        </table>
    </div>
)

// ─── Desktop row ───────────────────────────────────────────────────────────────

const renderDesktopRow = ({t, renderContextMenu, handleAction, visibleCols}) => (item, index) => {
    const {
        avatar, user, fullname, student_idcode,
        contact_phone, contact_email, gender, date_of_birth,
        father, mother,
    } = item ?? {}

    const isLinked = !!user?.id
    const studentAvatar = avatar || user?.avatar
    const fatherName = father?.user?.name || father?.name
    const fatherPhone = father?.phone
    const fatherLinked = !!father?.user?.id
    const motherName = mother?.user?.name || mother?.name
    const motherPhone = mother?.phone
    const motherLinked = !!mother?.user?.id

    return (
        <tr key={index} className="background hover:background-50 transition-colors border-t border-color-50">

            {/* # */}
            <td className="px-3 py-3 text-center text-xs text-color-400 font-medium">
                {index + 1}
            </td>

            {/* Học viên */}
            <td className="px-4 py-3">
                <div className="flex items-start gap-2">
                    <div className="relative flex-shrink-0 mt-0.5">
                        {renderIfElse(
                            studentAvatar,
                            <Avatar
                                size={36}
                                src={studentAvatar}
                                icon={<FaUserGraduate size={14} className="text-color-500"/>}
                                style={{border: '2px solid #fff'}}
                                className="flex flex-center background-200 shadow-md"
                            />,
                            <Avatar
                                size={36}
                                icon={<FaUserGraduate size={14} className="text-color-500"/>}
                                style={{border: '2px solid #fff'}}
                                className={classNames('flex flex-center background-200 shadow-md', !isLinked && 'opacity-60')}
                            />
                        )}
                        {isLinked && (
                            <Tooltip title={t('linked account')} placement="top">
                                <span
                                    style={{width: 16, height: 16, bottom: -4, left: -4}}
                                    className="flex flex-center background shadow rounded-full absolute">
                                    <FaLink size={10} className="text-green-500"/>
                                </span>
                            </Tooltip>
                        )}
                    </div>
                    <div className="flex flex-col min-w-0">
                        <span
                            onClick={() => handleAction(StudentActionTypes.STUDENT_DETAIL, item)}
                            className="font-semibold text-sm text-color-000 hover:text-primary cursor-pointer leading-snug">
                            {fullname}
                        </span>
                        <div className="flex items-center flex-wrap gap-2 text-xs text-color-400 mt-0.5">
                            {date_of_birth && (
                                <span className="flex items-center gap-0.5">
                                    <GoDotFill size={7}/>
                                    {formatDate(date_of_birth, 'DD/MM/YYYY')}
                                </span>
                            )}
                            {gender !== 0 && gender && (
                                <span className="flex items-center gap-0.5">
                                    <GoDotFill size={7}/>
                                    {t(getLabelOfGender(gender))}
                                </span>
                            )}
                        </div>
                        {isLinked ? (
                            <div className="flex items-center gap-1 mt-1">
                                <Avatar
                                    size={14}
                                    src={user?.avatar}
                                    icon={<FaUserGraduate size={7} className="text-color-500"/>}
                                    className="flex-shrink-0 flex flex-center background-100"/>
                                <span className="text-xs text-green-600 font-medium leading-tight truncate">
                                    {user?.name}
                                </span>
                            </div>
                        ) : (
                            <div className="flex items-center gap-1 mt-1 text-xs text-color-300 italic">
                                <FaLinkSlash size={10}/>
                                <span>{t('no linked account')}</span>
                            </div>
                        )}
                    </div>
                </div>
            </td>

            {/* Mã học viên */}
            {visibleCols.includes('student_code') && (
                <td className="px-3 py-3">
                    {student_idcode ? (
                        <span
                            onClick={() => {
                                copyToClipboard(student_idcode)
                                message.info(t('copied'))
                            }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md font-mono text-xs text-blue-600 cursor-pointer hover:bg-blue-100 transition-colors"
                            style={{background: 'rgba(59,130,246,0.08)'}}>
                            {student_idcode}
                            <IoCopyOutline size={10}/>
                        </span>
                    ) : (
                        <span className="text-xs text-color-300">—</span>
                    )}
                </td>
            )}

            {/* Phụ huynh */}
            {(visibleCols.includes('father') || visibleCols.includes('mother')) && (
                <td className="px-3 py-3">
                    {(fatherName || motherName) ? (
                        <div className="flex flex-col gap-2">
                            {fatherName && (
                                <div className="flex items-start gap-1">
                                    <span className="text-xs font-medium text-color-400 w-7 flex-shrink-0 pt-0.5 leading-none">
                                        {t('father')}:
                                    </span>
                                    <div className="flex flex-col min-w-0">
                                        <div className="flex items-center gap-1">
                                            {fatherLinked && (
                                                <Avatar
                                                    size={14}
                                                    src={father?.user?.avatar}
                                                    className="flex-shrink-0 flex flex-center background-100"/>
                                            )}
                                            <span className="text-sm font-medium text-color-100 leading-snug truncate">
                                                {fatherName}
                                            </span>
                                        </div>
                                        {fatherPhone && (
                                            <span className="flex items-center gap-0.5 text-xs text-blue-500 mt-0.5">
                                                <HiPhone size={10}/>{fatherPhone}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {motherName && (
                                <div className="flex items-start gap-1">
                                    <span className="text-xs font-medium text-color-400 w-7 flex-shrink-0 pt-0.5 leading-none">
                                        {t('mother')}:
                                    </span>
                                    <div className="flex flex-col min-w-0">
                                        <div className="flex items-center gap-1">
                                            {motherLinked && (
                                                <Avatar
                                                    size={14}
                                                    src={mother?.user?.avatar}
                                                    className="flex-shrink-0 flex flex-center background-100"/>
                                            )}
                                            <span className="text-sm font-medium text-color-100 leading-snug truncate">
                                                {motherName}
                                            </span>
                                        </div>
                                        {motherPhone && (
                                            <span className="flex items-center gap-0.5 text-xs text-blue-500 mt-0.5">
                                                <HiPhone size={10}/>{motherPhone}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        <span className="text-xs text-color-300">—</span>
                    )}
                </td>
            )}

            {/* Liên hệ */}
            {visibleCols.includes('contact') && (
                <td className="px-3 py-3">
                    {(contact_phone || contact_email) ? (
                        <div className="flex flex-col gap-0.5">
                            {contact_phone && (
                                <span className="flex items-center gap-1 text-xs text-blue-500">
                                    <HiPhone size={11}/>{contact_phone}
                                </span>
                            )}
                            {contact_email && (
                                <a
                                    href={`mailto:${contact_email}`}
                                    className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 no-underline"
                                    style={{maxWidth: 180}}>
                                    <HiEnvelope size={11} className="flex-shrink-0"/>
                                    <span className="truncate">{contact_email}</span>
                                </a>
                            )}
                        </div>
                    ) : (
                        <span className="text-xs text-color-300">—</span>
                    )}
                </td>
            )}

            {/* Actions */}
            <td className="px-2 py-3 text-center">
                {renderContextMenu(item)}
            </td>
        </tr>
    )
}

// ─── Mobile card ───────────────────────────────────────────────────────────────

const renderMobileCard = ({t, renderContextMenu, handleAction}) => (item, index) => {
    const {
        avatar, user, fullname, student_idcode,
        contact_phone, contact_email, gender, date_of_birth,
        father, mother,
    } = item ?? {}

    const isLinked = !!user?.id
    const studentAvatar = avatar || user?.avatar
    const fatherName = father?.user?.name || father?.name
    const fatherPhone = father?.phone
    const motherName = mother?.user?.name || mother?.name
    const motherPhone = mother?.phone

    return (
        <div key={index} className="px-4 py-3 flex gap-3 relative">
            <div className="flex-shrink-0 relative mt-0.5">
                {renderIfElse(
                    studentAvatar,
                    <Avatar
                        size={38}
                        src={studentAvatar}
                        icon={<FaUserGraduate size={15} className="text-color-500"/>}
                        style={{border: '2px solid #fff'}}
                        className="flex flex-center background-200 shadow-md"
                    />,
                    <Avatar
                        size={38}
                        icon={<FaUserGraduate size={15} className="text-color-500"/>}
                        style={{border: '2px solid #fff'}}
                        className={classNames('flex flex-center background-200 shadow-md', !isLinked && 'opacity-60')}
                    />
                )}
                {isLinked && (
                    <span
                        style={{width: 16, height: 16, bottom: -4, left: -4}}
                        className="flex flex-center background shadow rounded-full absolute">
                        <FaLink size={10} className="text-green-500"/>
                    </span>
                )}
            </div>

            <div className="flex flex-col flex-1 min-w-0 gap-1 pr-8">
                {/* Tên + mã */}
                <div className="flex items-center gap-2 flex-wrap">
                    <span
                        onClick={() => handleAction(StudentActionTypes.STUDENT_DETAIL, item)}
                        className="font-semibold text-sm text-color-000 hover:text-primary cursor-pointer leading-snug">
                        {fullname}
                    </span>
                    {student_idcode && (
                        <span
                            onClick={() => {
                                copyToClipboard(student_idcode)
                                message.info(t('copied'))
                            }}
                            className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded font-mono text-xs text-blue-600 cursor-pointer"
                            style={{background: 'rgba(59,130,246,0.08)'}}>
                            {student_idcode}
                            <IoCopyOutline size={9}/>
                        </span>
                    )}
                </div>

                {/* Ngày sinh + giới tính */}
                {(date_of_birth || (gender !== 0 && gender)) && (
                    <div className="flex items-center gap-2 text-xs text-color-400">
                        {date_of_birth && (
                            <span className="flex items-center gap-0.5">
                                <GoDotFill size={7}/>{formatDate(date_of_birth, 'DD/MM/YYYY')}
                            </span>
                        )}
                        {gender !== 0 && gender && (
                            <span className="flex items-center gap-0.5">
                                <GoDotFill size={7}/>{t(getLabelOfGender(gender))}
                            </span>
                        )}
                    </div>
                )}

                {/* Tài khoản liên kết */}
                {isLinked && (
                    <div className="flex items-center gap-1 text-xs text-green-600">
                        <BsPersonCheckFill size={10}/>
                        <span className="font-medium">{user?.name}</span>
                        {user?.email && (
                            <span className="text-color-400 italic truncate">· {user.email}</span>
                        )}
                    </div>
                )}

                {/* Phụ huynh */}
                {(fatherName || motherName) && (
                    <div className="mt-0.5 flex flex-col gap-0.5 pl-2 border-l-2 border-color-50">
                        {fatherName && (
                            <div className="flex items-center gap-1 text-xs text-color-400 flex-wrap">
                                <span className="font-medium text-color-300 flex-shrink-0">{t('father')}:</span>
                                <span className="text-color-100">{fatherName}</span>
                                {fatherPhone && (
                                    <span className="flex items-center gap-0.5 text-blue-500">
                                        <HiPhone size={9}/>{fatherPhone}
                                    </span>
                                )}
                            </div>
                        )}
                        {motherName && (
                            <div className="flex items-center gap-1 text-xs text-color-400 flex-wrap">
                                <span className="font-medium text-color-300 flex-shrink-0">{t('mother')}:</span>
                                <span className="text-color-100">{motherName}</span>
                                {motherPhone && (
                                    <span className="flex items-center gap-0.5 text-blue-500">
                                        <HiPhone size={9}/>{motherPhone}
                                    </span>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Liên hệ */}
                {(contact_phone || contact_email) && (
                    <div className="flex items-center gap-x-3 gap-y-0.5 flex-wrap text-xs text-blue-500">
                        {contact_phone && (
                            <span className="flex items-center gap-0.5">
                                <HiPhone size={10}/>{contact_phone}
                            </span>
                        )}
                        {contact_email && (
                            <a
                                href={`mailto:${contact_email}`}
                                className="flex items-center gap-0.5 no-underline text-blue-500 hover:text-blue-700 truncate">
                                <HiEnvelope size={10}/>{contact_email}
                            </a>
                        )}
                    </div>
                )}
            </div>

            <div className="absolute right-4 top-3 flex-shrink-0">
                {renderContextMenu(item)}
            </div>
        </div>
    )
}

// ─── Column toggle ─────────────────────────────────────────────────────────────

const ColumnToggle = ({visibleCols, onChange, t}) => {
    const content = (
        <div className="flex flex-col gap-0.5 py-1 min-w-36">
            {ALL_COLUMNS.filter(c => !c.alwaysVisible).map(col => (
                <label
                    key={col.key}
                    className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:background-100 rounded-md text-sm text-color-200">
                    <Checkbox
                        checked={visibleCols.includes(col.key)}
                        onChange={e => onChange(col.key, e.target.checked)}
                    />
                    {t(col.label)}
                </label>
            ))}
        </div>
    )
    return (
        <Popover
            trigger="click"
            placement="bottomRight"
            content={content}>
            <button
                className="flex items-center gap-1 px-2 py-1 text-xs font-medium text-color-300 border border-color-100 rounded-lg hover:background-100 hover:text-color-100 transition-colors">
                {t('show columns')}
                <BsLayoutThreeColumns size={13}/>
            </button>
        </Popover>
    )
}

// ─── Main ──────────────────────────────────────────────────────────────────────

const Parents = ({
    owner_id,
    owner_type,
    filter_actions = {
        sortBy: 'sort-by',
        keyword: 'keyword',
        course_id: 'course',
        course_class_id: 'course-class',
        sub_class_name: 'sub-class-name',
        include_archived: 'include-archived',
    },
}) => {
    const t = useTranslate()
    const location = useLocation()
    const {isLg} = useContext(LayoutContext)
    const [visibleCols, setVisibleCols] = useState(DEFAULT_VISIBLE_COLS)

    const handleColToggle = useCallback((key, checked) => {
        setVisibleCols(prev => checked ? [...prev, key] : prev.filter(k => k !== key))
    }, [])

    const sortBy = useRouteSearchParam(filter_actions.sortBy)
    const course_id = useRouteSearchParam(filter_actions.course_id)
    const course_class_id = useRouteSearchParam(filter_actions.course_class_id)
    const sub_class_name = useRouteSearchParam(filter_actions.sub_class_name)
    const include_archived = useRouteSearchParam(filter_actions.include_archived)

    const {refreshKey: formRefreshKey, modal, handleAction} = useStudentForm({owner_id, owner_type})

    const renderContextMenu = useCallback(
        (item) => createContextMenu(
            [
                {icon: <RiSearchEyeLine size={14} className="text-green-600"/>, label: 'view detail', key: StudentActionTypes.STUDENT_DETAIL},
                {icon: <BiSolidEdit size={14} className="text-blue-500"/>,      label: 'edit',        key: StudentActionTypes.STUDENT_UPDATE},
                {icon: <BiTrash size={14} className="text-primary-300"/>,       label: 'delete',      key: StudentActionTypes.STUDENT_DELETE},
            ].filter(() => item),
            (key) => handleAction(key, item)
        ),
        []
    )

    const refresh_deps = useMemo(
        () => [owner_id, owner_type, formRefreshKey, course_id, course_class_id, `refreshToken=${_.get(location.state, 'refreshToken')}`],
        [owner_id, owner_type, formRefreshKey, location.state]
    )

    const renderList = useCallback(
        ({keyword, sortBy: currentSort, filterBy, refreshKey}) => (
            <EntityList
                key={refreshKey}
                Header={({meta}) => meta}
                PaginationMeta={NullAsDefault}
                RenderEntity={React.Fragment}
                apiInfo={course_getStudents_Api}
                query={bindQueryParams([{id: owner_id}, {type: owner_type}])}
                values={{keyword, sort_by: currentSort, ...(filterBy ?? {})}}
                renderItem={isLg
                    ? renderDesktopRow({t, renderContextMenu, handleAction, visibleCols})
                    : renderMobileCard({t, renderContextMenu, handleAction})
                }
                renderLoading={isLg
                    ? renderTableLoading(visibleCols)
                    : renderCardLoading()
                }
                renderEmpty={isLg
                    ? () => (
                        <tr>
                            <td colSpan={2 + visibleCols.length + 1} className="py-12">
                                <EmptyHolder/>
                            </td>
                        </tr>
                    )
                    : () => <EmptyHolder/>
                }
                renderWrap={isLg
                    ? (child) => <ParentsTable t={t} visibleCols={visibleCols}>{child}</ParentsTable>
                    : (child) => (
                        <div className="mt-2 flex flex-col divide-y divide-color-50 background rounded-xl border border-color-50">
                            {child}
                        </div>
                    )
                }
            />
        ),
        [owner_id, owner_type, t, renderContextMenu, handleAction, visibleCols, isLg]
    )

    const [pager_widget] = usePaginationHelper({
        filter_params: {owner_id, owner_type},
        onInit: ({onSortChange = NullAsDefault, onFilterChange = NullAsDefault}) => {
            onFilterChange({course_id, course_class_id, sub_class_name, include_archived})
            onSortChange(sortBy)
        },
        renderList,
        SortByWidget: NullAsDefault,
        renderSortBy: NullAsDefault,
        dependencies: [owner_id, owner_type, course_id, course_class_id, sub_class_name, include_archived],
        query_params: filter_actions,
        withKey: createPagerKey(refresh_deps),
        AdvancedFilter: StudentFilter,
        isToggleDefault: true,
        renderHeader: ([searchBar, filterBar]) => (
            <div className="flex flex-col gap-3">
                {filterBar}
                {searchBar}
                <div className="flex items-center justify-end flex-wrap gap-3">
                    <span className="flex items-center gap-1 text-xs text-color-400 italic">
                        <FaLink size={12} className="text-green-500"/>
                        {t('linked account')}
                    </span>
                    <span className="flex items-center gap-1 text-xs text-color-400 italic">
                        <FaLinkSlash size={12} className="text-color-300"/>
                        {t('no linked account')}
                    </span>
                    {isLg && (
                        <ColumnToggle visibleCols={visibleCols} onChange={handleColToggle} t={t}/>
                    )}
                </div>
            </div>
        ),
    })

    return (
        <div className="flex flex-col gap-3">
            <div className="flex flex-col flex-1">
                <h2 className="font-bold text-xl text-gray-800 tracking-wide">
                    {t('parent management')}
                </h2>
                <div className="my-1 w-20 h-1 rounded-full bg-gray-400"/>
                <p className="mt-1 text-sm text-color-300 tracking-wider">
                    {t('manage the list of parents contact information for all students in the workspace')}
                </p>
            </div>
            {pager_widget}
            {modal}
            <div className="h-16"/>
        </div>
    )
}

export default compose(
    branch(
        ({owner_id, owner_type}) => _.some([owner_id, owner_type], _.isEmpty),
        () => () => <ContentNotFound/>
    )
)(Parents)
